import React from 'react';
import {Link} from 'react-router';
import classnames from 'classnames';
import {Icon} from '@albert-io/atomic';
import type {List, Map as ImmutableMap} from 'immutable';
import {callAction, useFluxStore} from '../../framework';
import subheaderActions from './SubheaderActions';
import subheaderStore from './SubheaderStore';
import './subheader.scss';

type SubheaderNavLink = ImmutableMap<string, string>;
type SubheaderNavLinks = List<SubheaderNavLink>;

type OnLinkClick = (link: SubheaderNavLink, e: React.MouseEvent) => void;

const noop = () => {};

interface SubheaderProps {
  className?: string;
  title?: string;
  links?: SubheaderNavLinks;
  subpath?: string;
  activePath?: string;
  exactMatch?: boolean;
  leftContent?: React.ReactNode;
  rightContent?: React.ReactNode;
  onLinkClick?: OnLinkClick;
}

// TODO: Refactor this component
export default function Subheader({
  className,
  title,
  links,
  subpath,
  activePath,
  exactMatch,
  leftContent,
  rightContent,
  onLinkClick = noop
}: SubheaderProps) {
  'use memo';
  const showMobileMenu = useFluxStore(subheaderStore, (store) => store.showMobileMenu());

  const subheaderClasses = classnames('subheader', className, {
    'subheader--has-links': links,
    'show-mobile-menu': showMobileMenu
  });

  return (
    <div className={subheaderClasses}>
      <div className='subheader__wrapper'>
        <div className='subheader__content'>
          {title ? <h1 className='subheader__title'>{title}</h1> : null}
          <SubheaderLinks
            subpath={subpath}
            activePath={activePath}
            exactMatch={exactMatch}
            links={links}
            onLinkClick={onLinkClick}
          />
        </div>
        <SubheaderCustomContent leftContent={leftContent} rightContent={rightContent} />
      </div>
    </div>
  );
}

interface SubheaderLinksProps {
  subpath?: string;
  activePath?: string;
  exactMatch?: boolean;
  links?: SubheaderNavLinks;
  onLinkClick?: OnLinkClick;
}

export function SubheaderLinks({
  subpath,
  activePath,
  exactMatch,
  links,
  onLinkClick
}: SubheaderLinksProps) {
  if (!links) {
    return null;
  }
  return (
    <div className='subheader__links'>
      {links.toArray().map((link, index) => (
        <SubheaderLink
          link={link}
          // oxlint-disable-next-line react/no-array-index-key -- subheader nav links, fixed set, not reordered
          key={index}
          subpath={subpath}
          activePath={activePath}
          exactMatch={exactMatch}
          onLinkClick={onLinkClick}
        />
      ))}
    </div>
  );
}

interface SubheaderLinkProps {
  link: SubheaderNavLink;
  subpath?: string;
  activePath?: string;
  exactMatch?: boolean;
  onLinkClick?: OnLinkClick;
}

function SubheaderLink({
  link,
  subpath = '',
  activePath = '',
  exactMatch = false,
  onLinkClick = noop
}: SubheaderLinkProps) {
  const linkName = link.get('name');
  if (!linkName) {
    return null;
  }

  const route = link.get('route');
  const path = `${subpath}${route}`;
  const isActive = path ? (exactMatch ? activePath === path : activePath.startsWith(path)) : false;

  const handleClick = (e: React.MouseEvent) => {
    callAction(subheaderActions.CLOSE_MOBILE_MENU);
    onLinkClick(link, e);
  };

  if (!path || isActive) {
    const linkClass = classnames('subheader__links-link', {active: isActive});
    return (
      <span className={linkClass} onClick={handleClick}>
        {linkName}
        {isActive && <Icon icon='chevron-down' className='subheader__links-link-caret' />}
      </span>
    );
  }

  return (
    <Link to={path} className='subheader__links-link' onClick={handleClick}>
      {linkName}
    </Link>
  );
}

interface SubheaderCustomContentProps {
  leftContent?: React.ReactNode;
  rightContent?: React.ReactNode;
}

function SubheaderCustomContent({leftContent, rightContent}: SubheaderCustomContentProps) {
  if (!leftContent && !rightContent) {
    return null;
  }
  return (
    <div className='subheader__custom-content-wrapper'>
      <div className='subheader__content subheader__custom-content--left'>
        {leftContent ? leftContent : null}
      </div>
      <div className='subheader__content subheader__custom-content--right'>
        {rightContent ? rightContent : null}
      </div>
    </div>
  );
}
